import { useEffect, useState, type RefObject } from "react";

/**
 * Measures the union bbox of `targets` inside `containerRef` and reports
 * which half of the container they currently sit in ("top" | "bottom").
 * Pair with a coach/caption card that renders on the OPPOSITE side, so the
 * card explaining a control never sits on top of that same control — the
 * concrete bug this fixes: a fixed bottom-anchored coach card covering the
 * exact rating buttons / attach icon it was pointing at whenever the target
 * happened to also be near the bottom of the screen. Shares the same
 * measure/poll cadence as `Spotlight` so both stay in sync.
 */
export function useSpotlightCoachSide(
  containerRef: RefObject<HTMLElement | null>,
  targets: RefObject<HTMLElement | null>[],
  active: boolean,
): "top" | "bottom" {
  const [side, setSide] = useState<"top" | "bottom">("bottom");

  useEffect(() => {
    if (!active) return;
    const measure = () => {
      const c = containerRef.current;
      if (!c) return;
      const cr = c.getBoundingClientRect();
      let top = Infinity;
      let bottom = -Infinity;
      let found = false;
      for (const ref of targets) {
        const el = ref.current;
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) continue;
        found = true;
        top = Math.min(top, r.top);
        bottom = Math.max(bottom, r.bottom);
      }
      if (!found) return;
      const midTarget = (top + bottom) / 2;
      const midContainer = cr.top + cr.height / 2;
      setSide(midTarget < midContainer ? "bottom" : "top");
    };
    measure();
    const id = window.setInterval(measure, 220);
    window.addEventListener("resize", measure);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, containerRef, ...targets]);

  return side;
}

/**
 * A reusable "spotlight" tour overlay: darkens everything inside `containerRef`
 * except a cutout rectangle around one or more `targets`' current on-screen
 * position(s). This measures the real elements via getBoundingClientRect()
 * so it stays correct across flex layouts, sidebar collapse toggles,
 * responsive widths, container resizes, etc. — no hardcoded pixel layout.
 *
 * Passing multiple `targets` draws ONE cutout that is the union bounding box
 * of all of them (not separate holes) — the right primitive when two
 * adjacent controls (e.g. a prompt textarea + its send button) need to read
 * as "both are the thing to look at right now".
 *
 * Purely decorative: pointer-events:none throughout, so the real control(s)
 * underneath still receive the click.
 */
export function Spotlight({
  containerRef,
  targets,
  radius = 12,
  pad = 6,
  active = true,
}: {
  containerRef: RefObject<HTMLElement | null>;
  targets: RefObject<HTMLElement | null>[];
  radius?: number;
  pad?: number;
  active?: boolean;
}) {
  const [rect, setRect] = useState<{ left: number; top: number; width: number; height: number } | null>(null);

  useEffect(() => {
    if (!active) {
      setRect(null);
      return;
    }
    const measure = () => {
      const c = containerRef.current;
      if (!c) return;
      const cr = c.getBoundingClientRect();
      let left = Infinity;
      let top = Infinity;
      let right = -Infinity;
      let bottom = -Infinity;
      let found = false;
      for (const ref of targets) {
        const el = ref.current;
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) continue;
        found = true;
        left = Math.min(left, r.left);
        top = Math.min(top, r.top);
        right = Math.max(right, r.right);
        bottom = Math.max(bottom, r.bottom);
      }
      if (!found) {
        setRect(null);
        return;
      }
      setRect({
        left: left - cr.left - pad,
        top: top - cr.top - pad,
        width: right - left + pad * 2,
        height: bottom - top + pad * 2,
      });
    };
    measure();
    // Elements here don't animate on their own — a light poll catches layout
    // shifts (sidebar collapse, chip rows appearing, etc.) without needing a
    // ResizeObserver wired to every possible ancestor.
    const id = window.setInterval(measure, 220);
    window.addEventListener("resize", measure);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, pad, ...targets]);

  if (!active || !rect) return null;
  return (
    <div className="cf-spotlight" aria-hidden>
      <div
        className="cf-spotlight-hole"
        style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height, borderRadius: radius }}
      />
    </div>
  );
}
